'use client';

// ============================================================================
// CrewAhead Maritime Platform - useAvailability Hook
// ============================================================================
// Manages availability posts: creating, updating, publishing, and browsing.
// Separates the candidate's own posts from the employer browse view.

import { useCallback, useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getMyAvailabilityPosts as getMyAvailabilityPostsApi,
  listAvailabilityPosts as listAvailabilityPostsApi,
  createAvailabilityPost as createAvailabilityPostApi,
  updateAvailabilityPost as updateAvailabilityPostApi,
  deleteAvailabilityPost as deleteAvailabilityPostApi,
  publishAvailabilityPost as publishAvailabilityPostApi,
} from '../lib/api';
import type { CrewAheadApiError } from '../lib/api/client';
import { QUERY_KEYS } from '../lib/types/api';
import { useAuthStore } from '../store/authStore';
import type {
  AvailabilityPost,
  AvailabilityPostFilters,
} from '../lib/types';
import type {
  CreateAvailabilityPostData,
  UpdateAvailabilityPostData,
} from '../lib/api/availability';

// ─── Hook Return Type ────────────────────────────────────────────────────────

export interface UseAvailabilityReturn {
  // ─── My Posts ─────────────────────────────────────────────────────────────
  myPosts: AvailabilityPost[];
  isMyPostsLoading: boolean;
  activePost: AvailabilityPost | null;

  // ─── Create / Update / Delete / Publish ───────────────────────────────────
  createPost: (data: CreateAvailabilityPostData) => Promise<AvailabilityPost>;
  updatePost: (id: string, data: UpdateAvailabilityPostData) => Promise<AvailabilityPost>;
  deletePost: (id: string) => Promise<void>;
  publishPost: (id: string) => Promise<AvailabilityPost>;
  isMutating: boolean;

  // ─── Browse (employer view) ───────────────────────────────────────────────
  posts: AvailabilityPost[];
  isPostsLoading: boolean;

  // ─── Filters ──────────────────────────────────────────────────────────────
  filters: AvailabilityPostFilters;
  setFilters: (filters: AvailabilityPostFilters) => void;

  // ─── Refetch ──────────────────────────────────────────────────────────────
  refetchMyPosts: () => Promise<void>;
}

// ─── useAvailability Hook ────────────────────────────────────────────────────

/**
 * Hook for managing availability posts and browsing.
 *
 * Provides separate queries for a candidate's own posts and the employer
 * browse view. All mutations invalidate the my-posts query so the UI stays
 * in sync.
 *
 * @param options - Enable browse mode and seed filters
 *
 * @example
 * ```tsx
 * // Candidate view
 * const { myPosts, activePost, createPost, publishPost } = useAvailability();
 *
 * // Employer browse view
 * const { posts, isPostsLoading, filters, setFilters } = useAvailability({
 *   enableBrowse: true,
 *   browseFilters: { rank: 'chief_officer' },
 * });
 * ```
 */
export function useAvailability(options?: {
  browseFilters?: AvailabilityPostFilters;
  enableBrowse?: boolean;
}): UseAvailabilityReturn {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // ─── Local State ───────────────────────────────────────────────────────────

  const [filters, setFilters] = useState<AvailabilityPostFilters>(
    options?.browseFilters ?? {}
  );

  // ─── My Posts Query ────────────────────────────────────────────────────────

  const {
    data: myPostsData,
    isLoading: isMyPostsLoading,
    refetch: refetchMyPostsQuery,
  } = useQuery({
    queryKey: QUERY_KEYS.AVAILABILITY.MY_POSTS,
    queryFn: getMyAvailabilityPostsApi,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes — posts change frequently
  });

  // ─── Browse Posts Query ────────────────────────────────────────────────────

  const {
    data: postsData,
    isLoading: isPostsLoading,
  } = useQuery({
    queryKey: QUERY_KEYS.AVAILABILITY.LIST(filters as Record<string, unknown>),
    queryFn: () => listAvailabilityPostsApi(filters),
    enabled: options?.enableBrowse === true,
    staleTime: 1000 * 60 * 2,
  });

  // ─── Create Mutation ───────────────────────────────────────────────────────

  const createMutation = useMutation<
    AvailabilityPost,
    CrewAheadApiError,
    CreateAvailabilityPostData
  >({
    mutationFn: createAvailabilityPostApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.AVAILABILITY.MY_POSTS,
      });
    },
  });

  // ─── Update Mutation ───────────────────────────────────────────────────────

  const updateMutation = useMutation<
    AvailabilityPost,
    CrewAheadApiError,
    { id: string; data: UpdateAvailabilityPostData }
  >({
    mutationFn: ({ id, data }) => updateAvailabilityPostApi(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.AVAILABILITY.MY_POSTS,
      });
    },
  });

  // ─── Delete Mutation ───────────────────────────────────────────────────────

  const deleteMutation = useMutation<void, CrewAheadApiError, string>({
    mutationFn: deleteAvailabilityPostApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.AVAILABILITY.MY_POSTS,
      });
    },
  });

  // ─── Publish Mutation ──────────────────────────────────────────────────────

  const publishMutation = useMutation<
    AvailabilityPost,
    CrewAheadApiError,
    string
  >({
    mutationFn: publishAvailabilityPostApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.AVAILABILITY.MY_POSTS,
      });
    },
  });

  // ─── Callback Wrappers ─────────────────────────────────────────────────────

  const createPost = useCallback(
    async (data: CreateAvailabilityPostData): Promise<AvailabilityPost> => {
      return createMutation.mutateAsync(data);
    },
    [createMutation]
  );

  const updatePost = useCallback(
    async (
      id: string,
      data: UpdateAvailabilityPostData
    ): Promise<AvailabilityPost> => {
      return updateMutation.mutateAsync({ id, data });
    },
    [updateMutation]
  );

  const deletePost = useCallback(
    async (id: string): Promise<void> => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );

  const publishPost = useCallback(
    async (id: string): Promise<AvailabilityPost> => {
      return publishMutation.mutateAsync(id);
    },
    [publishMutation]
  );

  const refetchMyPosts = useCallback(async (): Promise<void> => {
    await refetchMyPostsQuery();
  }, [refetchMyPostsQuery]);

  // ─── Derived State ─────────────────────────────────────────────────────────

  const myPosts = useMemo(() => myPostsData?.results ?? [], [myPostsData]);

  const activePost = useMemo(
    () => myPosts.find((p) => p.status === 'active') ?? null,
    [myPosts]
  );

  const posts = useMemo(() => postsData?.results ?? [], [postsData]);

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    publishMutation.isPending;

  return {
    // My posts
    myPosts,
    isMyPostsLoading,
    activePost,

    // CRUD
    createPost,
    updatePost,
    deletePost,
    publishPost,
    isMutating,

    // Browse
    posts,
    isPostsLoading,

    // Filters
    filters,
    setFilters,

    // Refetch
    refetchMyPosts,
  };
}
